import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) return toast.error("Passwords don't match");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset! Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <img src="/logo.png" alt="SmartHost Solutions" className="h-10 w-10 object-contain" />
          <span className="font-display text-xl font-bold">SmartHost Solutions</span>
        </div>

        {!token ? (
          <div data-testid="reset-no-token">
            <h2 className="font-display text-2xl font-semibold mb-2">Invalid reset link</h2>
            <p className="text-muted-foreground text-sm">
              This link is missing its token. Please request a new password reset.
            </p>
            <Link to="/forgot-password" className="text-sm text-primary mt-6 inline-block hover:underline">
              Request a new link
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-display text-2xl font-semibold mb-1">Set a new password</h2>
            <p className="text-muted-foreground text-sm mb-8">Choose a strong new password.</p>
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div className="space-y-2">
                <Label htmlFor="pw">New password</Label>
                <Input
                  id="pw"
                  type="password"
                  placeholder="••••••••"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input
                  id="confirm"
                  type="password"
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  data-testid="reset-confirm-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 text-base"
                disabled={loading}
                data-testid="reset-submit-button"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Reset password
              </Button>
            </form>
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground mt-6 hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
